import { Type } from '@sinclair/typebox';
import { JsonObjectSchema } from './common.js';
/**
 * The single runtime home of the seven document ingestion-status strings. The
 * TypeBox schema below, the ingestion worker's transition writes, and the
 * retrieval-eval corpus schema all consume THIS map — there is no second union
 * or status map anywhere (COP-115). A typo against a member (e.g.
 * `DOCUMENT_STATUS.extracing`) fails type-check.
 */
export const DOCUMENT_STATUS = {
    queued: 'queued',
    extracting: 'extracting',
    chunking: 'chunking',
    embedding: 'embedding',
    embedding_failed: 'embedding_failed',
    ready: 'ready',
    failed: 'failed',
};
export const DocumentStatusSchema = Type.Union([
    Type.Literal(DOCUMENT_STATUS.queued),
    Type.Literal(DOCUMENT_STATUS.extracting),
    Type.Literal(DOCUMENT_STATUS.chunking),
    Type.Literal(DOCUMENT_STATUS.embedding),
    Type.Literal(DOCUMENT_STATUS.embedding_failed),
    Type.Literal(DOCUMENT_STATUS.ready),
    Type.Literal(DOCUMENT_STATUS.failed),
]);
export const DocumentSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    // Nullable since M16 (HUB-074 §3, CONN-05): `trackId: null` marks a GLOBAL
    // (connector) document. Type-level breaking change for strict SDK consumers.
    trackId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    uploaderUserId: Type.String({ format: 'uuid' }),
    filename: Type.String(),
    mimeType: Type.String(),
    sizeBytes: Type.Integer(),
    storageKey: Type.String(),
    status: DocumentStatusSchema,
    extractedText: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    pageCount: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
    errorMessage: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    // M28 (ADM-14, COP-117): LLM-generated summary; `null` until the summarizer runs.
    summary: Type.Union([Type.String(), Type.Null()]),
    // HUB-094 §1 (DMET-05, milestone-32): free-form caller metadata, stored and
    // returned verbatim. Never parsed, validated or branched on by the hub — the
    // wire type is deliberately an OPEN object, not a closed key set.
    metadata: JsonObjectSchema,
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
// M28 (ADM-12, COP-117): the public "where does this document live" value —
// never exposes the Track UUID, only the human label. HUB-094 §1
// (DMET-05/DMET-07, milestone-32): the hub names no track-domain concept here —
// a caller that wants its own domain label reads it from the free-form
// `metadata` object. Mirrors `DocumentTrackEntity` in
// packages/hub/src/documents/lookup.ts (the one code home; this is its wire form).
export const DocumentTrackEntitySchema = Type.Union([
    Type.Object({ kind: Type.Literal('track'), track: Type.String() }),
    Type.Object({ kind: Type.Literal('global') }),
]);
// M28 (ADM-12): the processing-readiness pin returned by `documentReadiness`
// (packages/hub/src/documents/metadata.ts). `ready` and `searchable` are
// orthogonal — a failed document with retained text is searchable, never ready.
export const DocumentReadinessSchema = Type.Object({
    ready: Type.Boolean(),
    searchable: Type.Boolean(),
    status: DocumentStatusSchema,
    failureReason: Type.Union([Type.String(), Type.Null()]),
});
// M28 (ADM-12): the tuple-derived ownership/visibility summary returned by
// `describeDocumentAccess` (packages/hub/src/documents/metadata.ts). Viewer
// identities are deliberately NOT exposed here — only the count.
export const DocumentAccessSchema = Type.Object({
    ownerUserId: Type.Union([Type.String(), Type.Null()]),
    sharedWithTrack: Type.Boolean(),
    sharedWithEveryone: Type.Boolean(),
    viewerCount: Type.Integer(),
});
// M28 (ADM-12): the extended detail returned by GET /documents/:id — the full
// document row PLUS readiness + the tuple-derived access summary.
export const DocumentDetailSchema = Type.Composite([
    DocumentSchema,
    Type.Object({
        readiness: DocumentReadinessSchema,
        access: DocumentAccessSchema,
    }),
]);
// M28 (ADM-12): a LIGHT list item — deliberately omits `extractedText` (it can
// be megabytes) and every other heavy/ingestion-internal field. Origin/source
// is derivable from `trackEntity` + `sourceUrl`; a UI label is the screen's job.
export const DocumentListItemSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    filename: Type.String(),
    mimeType: Type.String(),
    sizeBytes: Type.Integer(),
    status: DocumentStatusSchema,
    summary: Type.Union([Type.String(), Type.Null()]),
    uploaderUserId: Type.String({ format: 'uuid' }),
    trackEntity: DocumentTrackEntitySchema,
    sourceUrl: Type.Union([Type.String(), Type.Null()]),
    // HUB-094 §1 (DMET-05): the row's free-form metadata, verbatim.
    metadata: JsonObjectSchema,
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
// M28 (ADM-12): optional filters for GET /documents. Absent `trackId` = every
// track ∪ global (the per-document `read` check does the scoping). `ownerUserId`
// filters the `uploader_user_id` column (owner == uploader for hub uploads).
// `filename` is a case-insensitive SUBSTRING match on the document name — the
// "which one was called X" question, distinct from POST /documents/search,
// which ranks document CONTENT.
export const DocumentListQuerySchema = Type.Object({
    status: Type.Optional(DocumentStatusSchema),
    trackId: Type.Optional(Type.String({ format: 'uuid' })),
    ownerUserId: Type.Optional(Type.String({ format: 'uuid' })),
    filename: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
export const DocumentListResponseSchema = Type.Object({
    items: Type.Array(DocumentListItemSchema),
    total: Type.Integer(),
});
export const UploadResponseSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    status: Type.Literal('queued'),
});
export const DocumentRetryResponseSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    status: Type.Literal('queued'),
});
// M28 (ADM-14, COP-117): the regenerated summary returned by the manual
// POST /documents/:id/summary retrigger. Non-null — the route 502s on a
// generation failure rather than returning a null summary.
export const DocumentSummaryResponseSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    summary: Type.String(),
});
export const DocumentIdParamsSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
});
// HUB-076 (DND-01/02): bucket selection for the track-agnostic POST /documents
// upload. Querystring (not body fields) because multipart bodies bypass TypeBox
// body validation. `trackId` OR `trackName` selects the track bucket (both
// present → 400); absent = GLOBAL bucket (`track_id IS NULL`).
// HUB-094 §1 (DMET-05, milestone-32 task-06): `metadata` rides the SAME
// querystring, and for the same reason — it is a JSON-ENCODED STRING because a
// multipart body cannot carry a typed object. The route JSON.parses it and
// rejects anything that is not a plain object with 400 `invalid_metadata`;
// that is a JSON-ness check, never an inspection of keys or values.
export const DocumentsCreateQuerySchema = Type.Object({
    trackId: Type.Optional(Type.String({ format: 'uuid' })),
    trackName: Type.Optional(Type.String({ minLength: 1 })),
    metadata: Type.Optional(Type.String({ minLength: 1 })),
});
export const ShareSchema = Type.Object({
    kind: Type.Union([Type.Literal('user'), Type.Literal('track'), Type.Literal('everyone')]),
    subjectId: Type.Optional(Type.String({ minLength: 1 })),
});
export const ShareResponseSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    kind: Type.Union([Type.Literal('user'), Type.Literal('track'), Type.Literal('everyone')]),
    subjectId: Type.Optional(Type.String()),
    createdAt: Type.String({ format: 'date-time' }),
});
export const SharesListResponseSchema = Type.Array(ShareResponseSchema);
export const ShareCreateResponseSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
});
export const ShareIdParamsSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    shareId: Type.String({ format: 'uuid' }),
});
// Broad document visibility scope (IMEM-09 / HUB-065). Orthogonal to per-user
// `viewer` shares: this sets the document to exactly one of the broad states.
export const DocumentScopeSchema = Type.Union([
    Type.Literal('private'),
    Type.Literal('track'),
    Type.Literal('everyone'),
]);
export const DocumentScopeParamsSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
});
export const DocumentScopeBodySchema = Type.Object({
    scope: DocumentScopeSchema,
});
// IMEM-09 / HUB-065 / COP-051 / COP-062 — in-stream confirmation envelope for the
// Copilot `share_document` write tool. NOT a Hub REST body: it rides the
// chat-message path so the widget and runtime share one parse contract (same
// carrier as MemoryEnvelopeSchema). Confirm/dismiss carry ONLY `suggestionId` —
// the server holds the whole binding (documentId, op, scope/targets) in its
// pending-suggestion store, so nothing consequential is echoed from the client.
// `tryParseDocShareEnvelope` validates every parse against this schema; the
// runtime confirm handler resolves the recorded suggestion before writing.
export const DocShareEnvelopeSchema = Type.Union([
    Type.Object({
        kind: Type.Literal('doc_share_confirm'),
        suggestionId: Type.String({ format: 'uuid' }),
    }),
    Type.Object({
        kind: Type.Literal('doc_share_dismiss'),
        suggestionId: Type.String({ format: 'uuid' }),
    }),
]);
// M28 (ADM-06, COP-117): read-only wire form of `documentPipelineSettings()`
// (packages/hub/src/ingestion/pipeline-config.ts) — the single read-only object
// the Admin "RAG settings" view serializes. Every knob is a call-time env read;
// nothing is persisted this milestone.
export const DocumentPipelineSettingsSchema = Type.Object({
    chunkSize: Type.Integer(),
    embeddingModelId: Type.String(),
    parserProvider: Type.String(),
    textractPollIntervalMs: Type.Integer(),
    textractMaxWaitMs: Type.Integer(),
    embeddingDim: Type.Integer(),
    rrfK: Type.Integer(),
});
//# sourceMappingURL=documents.js.map